import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { ChevronLeftIcon, XIcon } from "../icons";
import { KnowledgeGraph, STATE_LABEL } from "../components/KnowledgeGraph";
import { parseGraph, type GraphLesson, type GraphNode, type GraphState, type GraphView } from "../components/graphLayout.ts";
import { useElementWidth } from "../components/measure.ts";

const LEGEND: GraphState[] = ["known", "learning", "weak", "ready", "untested"];

/** A project's knowledge graph, read fresh from the server: the graph, none (no map or no script), or why it failed. */
export async function fetchGraph(id: string): Promise<{ graph: GraphView } | { none: true } | { error: string }> {
	try {
		const parsed = parseGraph(await api.projectGraph(id));
		return parsed.ok ? { graph: parsed.graph } : { error: parsed.error };
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return { none: true };
		return { error: err instanceof ApiError ? err.message : String(err) };
	}
}

/**
 * The knowledge-graph page of a project: the topic's concepts in stage bands across the whole middle,
 * lit by what the records show, and one concept's detail beside it. Fetched when it opens and again when
 * it comes back to the foreground, since a run in another tab may have changed the records.
 */
export function ProjectGraph({ projectId, onBack }: { projectId: string; onBack: () => void }) {
	const [view, setView] = useState<GraphView | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [selected, setSelected] = useState<string | null>(null);
	const [box, width] = useElementWidth(360);
	const scroller = useRef<HTMLDivElement | null>(null);

	const load = useCallback(async () => {
		const found = await fetchGraph(projectId);
		if ("graph" in found) setView(found.graph), setError(null);
		else if ("none" in found) setView(null), setError("暂无图谱");
		else setError(`图谱生成失败：${found.error}`);
	}, [projectId]);

	useEffect(() => {
		void load();
		const onShow = () => document.visibilityState === "visible" && void load();
		document.addEventListener("visibilitychange", onShow);
		return () => document.removeEventListener("visibilitychange", onShow);
	}, [load]);

	const jumpTo = useCallback((slug: string) => {
		setSelected(slug);
		requestAnimationFrame(() => scroller.current?.querySelector(`[data-slug="${CSS.escape(slug)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }));
	}, []);

	const node = view && selected ? view.nodes.find((n) => n.slug === selected) ?? null : null;
	const known = view ? view.nodes.filter((n) => n.state === "known").length : 0;

	return (
		<main className="chat project kg-page">
			<header className="chat-head page-head">
				<button className="ghost icon-btn" aria-label="返回" onClick={onBack}>
					<ChevronLeftIcon size={22} />
				</button>
				<div className="titles">
					<span className="title">知识图谱</span>
					{view && <span className="sub">{`已掌握 ${known}/${view.nodes.length}`}</span>}
				</div>
			</header>
			<div className={`kg-body${node ? " with-detail" : ""}`}>
				<div className="kg-scroll" ref={scroller}>
					<ul className="kg-legend">
						{LEGEND.map((state) => (
							<li key={state}>
								<span className={`kg-swatch ${state}`} />
								{STATE_LABEL[state]}
							</li>
						))}
					</ul>
					<div className="kg-canvas" ref={box}>
						{view ? <KnowledgeGraph view={view} width={width} selected={selected} onSelect={setSelected} /> : <p className="hint">{error ?? "加载中…"}</p>}
					</div>
					{view && error && <p className="banner error">{error}</p>}
				</div>
				{node && view && <Detail projectId={projectId} node={node} view={view} onJump={jumpTo} onClose={() => setSelected(null)} />}
			</div>
		</main>
	);
}

function Detail({ projectId, node, view, onJump, onClose }: { projectId: string; node: GraphNode; view: GraphView; onJump: (slug: string) => void; onClose: () => void }) {
	const [expanded, setExpanded] = useState(false);
	const drag = useRef<number | null>(null);
	const links = (
		[
			["前置", node.requires],
			["后续", node.requiredBy],
			["相关", node.relates],
		] as const
	).filter(([, slugs]) => slugs.length > 0);

	return (
		<aside className={`kg-detail${expanded ? " expanded" : ""}`} aria-label={node.name}>
			<div
				className="kg-handle"
				onPointerDown={(e) => (drag.current = e.clientY)}
				onPointerUp={(e) => {
					if (drag.current === null) return;
					const moved = e.clientY - drag.current;
					drag.current = null;
					if (moved < -40) setExpanded(true);
					else if (moved > 40) expanded ? setExpanded(false) : onClose();
					else setExpanded((v) => !v);
				}}
			/>
			<div className="kg-detail-head">
				<h4>{node.name}</h4>
				<span className={`kg-state ${node.state}`}>{STATE_LABEL[node.state]}</span>
				<button className="ghost icon-btn" aria-label="关闭" onClick={onClose}>
					<XIcon size={18} />
				</button>
			</div>
			{node.band === "pre" && node.field && <p className="hint">{node.field}</p>}
			{node.body && <p className="kg-body-text">{node.body}</p>}
			{node.lessons.length > 0 && (
				<section>
					<h5>课程历史</h5>
					<ul className="kg-lessons">
						{node.lessons.map((lesson) => (
							<Lesson key={lesson.section} projectId={projectId} lesson={lesson} />
						))}
					</ul>
				</section>
			)}
			{links.length > 0 && (
				<dl className="kg-links">
					{links.map(([label, slugs]) => (
						<div key={label}>
							<dt>{label}</dt>
							<dd>
								{slugs.map((slug) => {
									const target = view.nodes.find((n) => n.slug === slug);
									return (
										<button key={slug} className={`kg-chip ${target?.state ?? "untested"}`} onClick={() => onJump(slug)}>
											{target?.name ?? slug}
										</button>
									);
								})}
							</dd>
						</div>
					))}
				</dl>
			)}
		</aside>
	);
}

/** A section that taught the concept: its page opens in a new tab, read through the project. */
function Lesson({ projectId, lesson }: { projectId: string; lesson: GraphLesson }) {
	const when = lesson.learned ? lesson.learned.slice(5).replace("-", "/") : lesson.status === "learned" || lesson.status === "known" ? "" : "进行中";
	const href = lesson.page ? `/api/projects/${encodeURIComponent(projectId)}/files/${lesson.page.split("/").map(encodeURIComponent).join("/")}` : null;
	return (
		<li>
			{href ? (
				<a href={href} target="_blank" rel="noopener">
					{lesson.name}
				</a>
			) : (
				<span>{lesson.name}</span>
			)}
			{when && <span className="kg-when">{when}</span>}
		</li>
	);
}
